/**
 * Strutture API Queries
 * Centralized data fetching for Strutture feature
 */

import { supabase, getCurrentUserId } from '../client';
import type { 
  Struttura, StrutturaInsert, StrutturaUpdate,
  Camera, CameraInsert, CameraUpdate,
  QueryOptions 
} from '@/types';
const TABLE = 'strutture';

// ============================================================
// GET Queries
// ============================================================

export async function getStrutture(options?: QueryOptions & { userId: string }) {
  const userId = options?.userId || await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  let query = supabase
    .from(TABLE)
    .select('*', { count: options?.count })
    .eq('user_id', userId);

  // Apply filters
  if (options?.filters?.eq) {
    Object.entries(options.filters.eq).forEach(([key, value]) => {
      query = query.eq(key, value);
    });
  }

  // Apply ordering
  if (options?.order) {
    query = query.order(options.order.column, { 
      ascending: options.order.ascending ?? true 
    });
  }

  // Apply pagination
  if (options?.limit) {
    query = query.limit(options.limit);
  }
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
  }

  const { data, error, count } = await query;

  return {
    data: data as Struttura[] | null,
    error,
    count,
  };
}

export async function getStrutturaById(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('id', id)
    .eq('user_id', uid)
    .single();

  return {
    data: data as Struttura | null,
    error,
  };
}

export async function getStruttureAttive() {
  return getStrutture({
    filters: { eq: { stato: 'attiva' } },
    order: { column: 'nome', ascending: true },
  });
}

// ============================================================
// POST Queries
// ============================================================

export async function createStruttura(struttura: StrutturaInsert, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .insert({ ...struttura, user_id: uid })
    .select()
    .single();

  return {
    data: data as Struttura | null,
    error,
  };
}

// ============================================================
// PATCH Queries
// ============================================================

export async function updateStruttura(id: string, updates: StrutturaUpdate, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .update(updates)
    .eq('id', id)
    .eq('user_id', uid)
    .select()
    .single();

  return {
    data: data as Struttura | null,
    error,
  };
}

// ============================================================
// DELETE Queries
// ============================================================

export async function deleteStruttura(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { error } = await supabase
    .from(TABLE)
    .delete()
    .eq('id', id)
    .eq('user_id', uid);

  return { error };
}

// ============================================================
// Camere Queries (nested resource)
// ============================================================

export async function getCamereByStruttura(strutturaId: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.CAMERE)
    .select('*')
    .eq('struttura_id', strutturaId)
    .eq('user_id', uid)
    .order('nome', { ascending: true });

  return {
    data: data as Camera[] | null,
    error,
  };
}

export async function createCamera(camera: CameraInsert, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.CAMERE)
    .insert({ ...camera, user_id: uid })
    .select()
    .single();

  return {
    data: data as Camera | null,
    error,
  };
}

export async function updateCamera(id: string, updates: CameraUpdate, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(DatabaseTable.CAMERE)
    .update(updates)
    .eq('id', id)
    .eq('user_id', uid)
    .select()
    .single();

  return {
    data: data as Camera | null,
    error,
  };
}

export async function deleteCamera(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { error } = await supabase
    .from(DatabaseTable.CAMERE)
    .delete()
    .eq('id', id)
    .eq('user_id', uid);

  return { error };
}

// ============================================================
// Stats & Analytics
// ============================================================

export async function getStruttureCount(userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { count, error } = await supabase
    .from(TABLE)
    .select('*', { count: 'exact', head: true })
    .eq('user_id', uid);

  return { count, error };
}

export async function getOccupazioneStrutture() {
  const { data: strutture, error: struttureError } = await getStrutture();
  if (struttureError || !strutture) return { data: null, error: struttureError };

  const stats = await Promise.all(
    strutture.map(async (s) => {
      const { data: camere, error: camereError } = await supabase
        .from(DatabaseTable.CAMERE)
        .select('*')
        .eq('struttura_id', s.id);

      if (camereError) return { ...s, camereTotali: 0, camereDisponibili: 0 };

      const camereTotali = camere?.length || 0;
      const camereDisponibili = camere?.filter((c: Camera) => c.stato === 'Disponibile').length || 0;

      return {
        ...s,
        camereTotali,
        camereDisponibili,
        occupazione: camereTotali > 0 ? ((camereTotali - camereDisponibili) / camereTotali) * 100 : 0,
      };
    })
  );

  return { data: stats, error: null };
}
